import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getTodayLocal } from '@/lib/today';

interface IncomeSource {
  name: string;
  amount: number;
  frequency: string;
  nextPayment?: string;
}

interface RecurringExpense {
  name: string;
  amount: number;
  category: string;
  frequency: string;
  startDate?: string; // When this expense first starts/is due
}

interface SavingsGoal {
  title: string;
  target: number;
}

interface OnboardingParams {
  name: string;
  currency: string;
  startingBalance: number;
  monthlyBudget: number;
  resetDay: number;
  coachTone: string;
  primaryIncome: IncomeSource;
  secondaryIncomes: IncomeSource[];
  recurringExpenses: RecurringExpense[];
  goals: SavingsGoal[];
  dashboardViewMode?: string;
  whatsappNumber?: string | null;
  whatsappVerified?: boolean;
  petName?: string | null;
}

export function useFirstTimeUser() {
  const { user } = useAuth();
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  useEffect(() => {
    const checkFirstTimeUser = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Check if user has budget data (indicates they've been set up)
        const { data: budget } = await supabase
          .from('budgets')
          .select('id, income, monthly_budget')
          .eq('user_id', user.id)
          .maybeSingle();

        // Check if user has any expenses (indicates they've started using the app)
        const { data: expenses, count } = await supabase
          .from('expenses')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        // Check if user has any goals
        const { data: goals, count: goalsCount } = await supabase
          .from('goals')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        // Check if user is a contributor on any shared goal
        const { count: contributorCount } = await supabase
          .from('goal_contributors')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        // User is first-time if:
        // 1. No budget OR budget has default values (income=5000, monthly_budget=3000)
        // 2. No expenses
        // 3. No goals (owned or contributed)
        const hasDefaultBudget = budget && budget.income === 5000 && budget.monthly_budget === 3000;
        const hasNoExpenses = !count || count === 0;
        const hasNoGoals = !goalsCount || goalsCount === 0;
        const isContributor = contributorCount && contributorCount > 0;

        const isNew = (!budget || hasDefaultBudget) && hasNoExpenses && hasNoGoals && !isContributor;
        
        setIsFirstTime(isNew);
        setHasCompletedOnboarding(!isNew);
      } catch (error) {
        console.error('Error checking first-time user:', error);
        setIsFirstTime(false);
      } finally {
        setLoading(false);
      }
    };

    checkFirstTimeUser();
  }, [user]);

  const completeOnboarding = async (data: OnboardingParams) => {
    if (!user) return { error: 'Not authenticated' };

    try {
      // Calculate total income from all sources
      const totalIncome = data.primaryIncome.amount + 
        data.secondaryIncomes.reduce((sum, inc) => sum + inc.amount, 0);

      // 1. Update budget with all fields
      const { error: budgetError } = await supabase
        .from('budgets')
        .upsert({
          user_id: user.id,
          income: totalIncome,
          monthly_budget: data.monthlyBudget,
          reset_day: data.resetDay,
          starting_balance: data.startingBalance
        }, { onConflict: 'user_id' });

      if (budgetError) throw budgetError;

      // 2. Update profile with name, currency, coach tone, budget mode, and optional WhatsApp
      const profilePayload: Record<string, any> = {
        user_id: user.id,
        display_name: data.name,
        currency: data.currency,
        coach_tone: data.coachTone,
        dashboard_view_mode: data.dashboardViewMode || 'simple',
      };
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(profilePayload as any, { onConflict: 'user_id' });

      if (profileError) {
        console.error('Profile update error:', profileError);
      }

      // Persist the user's chosen view mode locally so the Dashboard's first
      // paint matches it (avoids a flash of Expert when localStorage is stale
      // from a previous session).
      try {
        localStorage.setItem('fennest_dashboard_view_mode', data.dashboardViewMode || 'simple');
      } catch {
        /* ignore storage errors (e.g. private mode) */
      }

      // 3. Delete existing income sources to prevent duplicates (onboarding replaces all)
      await supabase
        .from('income_sources')
        .delete()
        .eq('user_id', user.id);

      // 4. Insert primary income into income_sources
      const { error: primaryIncomeError } = await supabase
        .from('income_sources')
        .insert({
          user_id: user.id,
          source_name: data.primaryIncome.name,
          amount: data.primaryIncome.amount,
          frequency: data.primaryIncome.frequency,
          next_payment_date: data.primaryIncome.nextPayment || null,
          is_recurring: true,
          is_active: true,
          category: 'primary'
        });

      if (primaryIncomeError) {
        console.error('Primary income insert error:', primaryIncomeError);
      }

      // 5. Insert secondary incomes
      if (data.secondaryIncomes.length > 0) {
        const secondaryIncomesToInsert = data.secondaryIncomes
          .filter(inc => inc.name && inc.amount > 0)
          .map(inc => ({
            user_id: user.id,
            source_name: inc.name,
            amount: inc.amount,
            frequency: inc.frequency,
            next_payment_date: inc.nextPayment || null,
            is_recurring: true,
            is_active: true,
            category: 'secondary'
          }));

        if (secondaryIncomesToInsert.length > 0) {
          const { error: secondaryIncomeError } = await supabase
            .from('income_sources')
            .insert(secondaryIncomesToInsert);

          if (secondaryIncomeError) {
            console.error('Secondary income insert error:', secondaryIncomeError);
          }
        }
      }

      // 6. Delete existing recurring expenses to prevent duplicates (onboarding replaces all)
      await supabase
        .from('expenses')
        .delete()
        .eq('user_id', user.id)
        .eq('is_recurring', true);

      // 7. Insert recurring expenses
      if (data.recurringExpenses.length > 0) {
        const today = getTodayLocal();
        const expensesToInsert = data.recurringExpenses
          .filter(exp => exp.name && exp.amount > 0)
          .map(exp => {
            const expenseDate = exp.startDate || today;
            return {
              user_id: user.id,
              description: exp.name,
              amount: exp.amount,
              category: exp.category,
              frequency: exp.frequency,
              is_recurring: true,
              is_active: true,
              expense_date: expenseDate,
              next_due_date: expenseDate
            };
          });

        if (expensesToInsert.length > 0) {
          const { error: expensesError } = await supabase
            .from('expenses')
            .insert(expensesToInsert);

          if (expensesError) {
            console.error('Recurring expenses insert error:', expensesError);
          }
        }
      }

      // 6. Insert goals
      if (data.goals.length > 0) {
        const goalsToInsert = data.goals
          .filter(goal => goal.title && goal.target > 0)
          .map(goal => ({
            user_id: user.id,
            title: goal.title,
            target_amount: goal.target,
            current_amount: 0,
            is_shared: false,
            is_premium: false
          }));

        if (goalsToInsert.length > 0) {
          const { error: goalsError } = await supabase
            .from('goals')
            .insert(goalsToInsert);

          if (goalsError) {
            console.error('Goals insert error:', goalsError);
          }
        }
      }

      // 7. Set pet name if provided
      if (data.petName && data.petName.trim().length > 0) {
        try {
          await supabase.rpc('rename_pet' as any, { p_pet_name: data.petName.trim() });
        } catch (e) {
          console.error('Pet name set error:', e);
        }
      }

      setIsFirstTime(false);
      setHasCompletedOnboarding(true);

      return { error: null };
    } catch (error) {
      console.error('Error completing onboarding:', error);
      return { error: 'Failed to complete onboarding' };
    }
  };

  return {
    isFirstTime,
    loading,
    hasCompletedOnboarding,
    completeOnboarding
  };
}
